import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import HighchartsStock from 'highcharts/modules/stock';
import moment from 'moment-timezone';
import './Highcharts.less';
import { toSnakeCase } from '../../../helpers/utils';
import { usePersistedFiltering } from '../../../contexts/FilterContext';

if (typeof Highcharts === 'object') {
    HighchartsExporting(Highcharts);
    HighchartsStock(Highcharts);
    HighchartsAccessibility(Highcharts);
}

const MultiLineChart = ({ config = {}, persistConfig = {}, type }) => {
    const { series, title, yAxisTitle, largeChartConfig = {}, isDashboardWidget, height, xAxisTitle, titleStyle, ...restConfig } = config;

    const { pageKey, chartId = toSnakeCase(title?.label) } = persistConfig;
    const { getFiltersForPage, setFiltersForPage } = usePersistedFiltering();
    const pageContext = pageKey && getFiltersForPage(pageKey);

    const defaultConfig = useMemo(
        () => ({
            chart: {
                type: type || 'line',
                zoomType: 'x',
                panning: true,
                panKey: 'shift',
                height: isDashboardWidget ? height : 450,
                events: {
                    selection: function (event) {
                        if (pageKey && pageContext) {
                            if (event.resetSelection) {
                                setFiltersForPage(pageKey, { ...pageContext, [chartId]: {} });
                            } else {
                                setFiltersForPage(pageKey, {
                                    ...pageContext,
                                    [chartId]: { zoomMin: event.xAxis[0].min, zoomMax: event.xAxis[0].max }
                                });
                            }
                        }
                    },
                    load: function () {
                        if (pageKey && pageContext && pageContext[chartId]?.zoomMin && pageContext[chartId]?.zoomMax) {
                            this.xAxis[0].setExtremes(parseFloat(pageContext[chartId].zoomMin), parseFloat(pageContext[chartId].zoomMax));
                            this.showResetZoom();
                        }
                    }
                }
            },
            title: {
                text: isDashboardWidget
                    ? null
                    : title
                    ? `${title?.label
                          .split('/')
                          .map((part, index) => (index !== 0 ? `<span style="font-weight: normal">${part}</span>` : part))
                          .join('/')}`
                    : 'Multi-Line Chart',
                align: 'left'
            },
            xAxis: {
                type: 'datetime',
                minRange: 1,
                labels: {
                    align: 'center',
                    autoRotation: [0, -10, -20, -30, -40, -50, -60, -70, -80, -90],
                    style: {
                        textOverflow: 'none',
                        whiteSpace: 'nowrap'
                    }
                },
                dateTimeLabelFormats: {
                    minute: '%I:%M %P',
                    hour: '%I:%M %P',
                    day: '%m/%d/%Y',
                    week: '%m/%d/%Y',
                    month: '%m/%Y',
                    year: '%Y'
                },
                title: {
                    text: xAxisTitle || null,
                    style: isDashboardWidget ? titleStyle : {}
                },
                events: {
                    afterSetExtremes: function (e) {
                        const chart = this.chart;
                        const defaultMin = chart.xAxis[0].dataMin;
                        const defaultMax = chart.xAxis[0].dataMax;
                
                        // Check if the chart is zoomed in or has userMin/userMax setx
                        if (e.min !== undefined && e.max !== undefined && e.userMax && e.userMin) {
                            const defaultMin = chart.xAxis[0].dataMin;
                            const isZoomedIn = (e.min !== defaultMin || e.max !== defaultMax);
                            const hasUserExtremes = (e.userMin !== undefined && e.userMax !== undefined);
                            if (isZoomedIn || hasUserExtremes) {
                                if (chart.resetZoomButton) {
                                    chart.resetZoomButton.show();
                                } else {
                                    chart.showResetZoom();
                                }
                            } else {
                                // Hide the reset zoom button if back to default
                                if (chart.resetZoomButton) {
                                    chart.resetZoomButton.hide();
                                }
                            }
                        } else {
                            // Hide the reset zoom button if no extremes set
                            if (chart.resetZoomButton) {
                                chart.resetZoomButton.hide();
                            }
                        }
                    }
                }
            },
            yAxis: {
                title: {
                    text: yAxisTitle || 'Values',
                    align: 'middle',
                    style: isDashboardWidget ? titleStyle : {}
                },
                max: (title?.units ?? '') === 'Percent' ? 100 : undefined
            },
            tooltip: {
                shared: true,
                formatter: function () {
                    const dateTime = '<div style="font-size: 0.8em">' + moment.utc(this.x).format('dddd, MMM D, hh:mm a') + '</div>';
                    let content = dateTime;
                    this.points.forEach((point) => {
                        let value = point.y.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        if (yAxisTitle === 'Percent') {
                            value += '%';
                        }
                        content += '<br/><span style="color:' + point.color + '">●</span> ' + point.series.name + ': <b>' + value + '</b>';
                    });
                    return content;
                }
            },
            series: series?.map((series) => ({
                name: series.name,
                data: series.data?.map((point) => [parseInt(point.time, 10) || parseInt(point.timestamp, 10), point.value]).sort((a, b) => a[0] - b[0])
            })),
            plotOptions: {
                series: {
                    marker: {
                        enabled: false
                    },
                    dataGrouping: { enabled: false },
                    showInNavigator: true
                }
            },
            legend: {
                enabled: !isDashboardWidget
            },
            exporting: {
                enabled: !isDashboardWidget,
                sourceWidth: 1100,
                printMaxWidth: 1100,
                chartOptions: {
                    title: {
                        text: title.label || 'Multi-Line Chart'
                    }
                },
                filename: title.label || 'Multi-Line Chart'
            },
            ...largeChartConfig
        }),
        [type, isDashboardWidget, height, title, xAxisTitle, yAxisTitle, series, largeChartConfig, titleStyle]
    );

    const mergedConfig = { ...defaultConfig, ...restConfig };

    return <HighchartsReact highcharts={Highcharts} options={mergedConfig} />;
};

export default MultiLineChart;
